import { motion } from "framer-motion";
import { useState } from "react";
import { FaEnvelope, FaShieldAlt, FaChartLine, FaUsers } from "react-icons/fa";

export default function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setEmail("");
      // Reset after 3 seconds
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  const benefits = [
    {
      icon: FaShieldAlt,
      title: "Security Updates",
      description: "Stay informed about the latest security enhancements and compliance updates"
    },
    {
      icon: FaChartLine,
      title: "Performance Insights",
      description: "Get monthly reports on platform performance and new feature releases"
    },
    {
      icon: FaUsers,
      title: "Industry News",
      description: "Receive updates on insurance industry trends and best practices"
    }
  ];

  return (
    <section className="overflow-x-hidden px-4 py-20 bg-gradient-to-br from-gray-50 to-white sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 mb-6 text-sm font-medium text-blue-700 bg-blue-100 rounded-full"
            >
              <FaEnvelope size={16} className="mr-2" />
              Stay Updated
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mb-6 text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl"
            >
              Stay Ahead with Industry Insights
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mb-8 text-lg leading-relaxed text-gray-600"
            >
              Subscribe to our newsletter and get exclusive access to industry insights, platform updates, and expert tips to grow your insurance business.
            </motion.p>

            {/* Benefits */}
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
                    <benefit.icon size={16} className="text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                    <p className="text-sm text-gray-600">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Newsletter Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative"
          >
            <div className="p-8 bg-white rounded-2xl ring-1 ring-gray-200 shadow-xl">
              {!isSubscribed ? (
                <>
                  <h3 className="mb-6 text-2xl font-bold text-center text-gray-900">
                    Subscribe to Our Newsletter
                  </h3>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-700">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your business email"
                        className="px-4 py-3 w-full rounded-xl border border-gray-300 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="px-6 py-3 w-full font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-lg transition-all duration-300 hover:from-blue-700 hover:to-indigo-800 hover:shadow-xl"
                    >
                      Subscribe Now
                    </motion.button>
                  </form>

                  <p className="mt-4 text-xs text-center text-gray-500">
                    By subscribing, you agree to our privacy policy and terms of service.
                  </p>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-8 text-center"
                >
                  <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full">
                    <div className="flex justify-center items-center w-8 h-8 bg-green-500 rounded-full">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-900">
                    Successfully Subscribed!
                  </h3>
                  <p className="text-gray-600">
                    Thank you for subscribing. You'll receive our next newsletter soon.
                  </p>
                </motion.div>
              )}
            </div>

            {/* Floating Elements */}
            <motion.div
              animate={{ 
                y: [0, -10, 0],
                rotate: [0, 5, 0]
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -top-4 -right-4 w-24 h-24 bg-blue-100 rounded-full opacity-50"
            />
            <motion.div
              animate={{ 
                y: [0, 10, 0],
                rotate: [0, -5, 0]
              }}
              transition={{ 
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="absolute -bottom-4 -left-4 w-20 h-20 bg-indigo-100 rounded-full opacity-50"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
